/**
 * Tiny safe arithmetic evaluator for the in-game calculator: + − × ÷, brackets, decimals with a
 * comma or a dot, unary minus. No `eval`, no variables. Returns null for anything invalid.
 */

type Token =
  { kind: "num"; value: number } | { kind: "op"; op: "+" | "-" | "*" | "/" } | { kind: "(" } | { kind: ")" };

function tokenize(input: string): Token[] | null {
  if (/\d\s+[\d.,]/.test(input)) return null; // "2 3" is not a number
  const s = input
    .replace(/\s+/g, "")
    .replace(/,/g, ".")
    .replace(/[×·]/g, "*")
    .replace(/[÷:]/g, "/")
    .replace(/[−–]/g, "-");
  const tokens: Token[] = [];
  let i = 0;
  while (i < s.length) {
    const ch = s[i] as string;
    if (/[0-9.]/.test(ch)) {
      let j = i;
      while (j < s.length && /[0-9.]/.test(s[j] as string)) j++;
      const text = s.slice(i, j);
      if ((text.match(/\./g) ?? []).length > 1 || text === ".") return null;
      tokens.push({ kind: "num", value: Number(text) });
      i = j;
    } else if (ch === "+" || ch === "-" || ch === "*" || ch === "/") {
      tokens.push({ kind: "op", op: ch });
      i++;
    } else if (ch === "(") {
      tokens.push({ kind: "(" });
      i++;
    } else if (ch === ")") {
      tokens.push({ kind: ")" });
      i++;
    } else {
      return null;
    }
  }
  return tokens;
}

export function evaluate(input: string): number | null {
  const tokens = tokenize(input);
  if (!tokens || tokens.length === 0) return null;
  let pos = 0;
  const peek = () => tokens[pos];
  const next = () => tokens[pos++];

  const factor = (): number | null => {
    const t = next();
    if (!t) return null;
    if (t.kind === "num") return t.value;
    if (t.kind === "op" && t.op === "-") {
      const v = factor();
      return v === null ? null : -v;
    }
    if (t.kind === "op" && t.op === "+") return factor();
    if (t.kind === "(") {
      const v = expr();
      const close = next();
      if (v === null || !close || close.kind !== ")") return null;
      return v;
    }
    return null;
  };
  const term = (): number | null => {
    let v = factor();
    if (v === null) return null;
    for (;;) {
      const t = peek();
      if (!t || t.kind !== "op" || (t.op !== "*" && t.op !== "/")) return v;
      next();
      const r = factor();
      if (r === null) return null;
      if (t.op === "/") {
        if (r === 0) return null;
        v = v / r;
      } else v = v * r;
    }
  };
  const expr = (): number | null => {
    let v = term();
    if (v === null) return null;
    for (;;) {
      const t = peek();
      if (!t || t.kind !== "op" || (t.op !== "+" && t.op !== "-")) return v;
      next();
      const r = term();
      if (r === null) return null;
      v = t.op === "+" ? v + r : v - r;
    }
  };
  const result = expr();
  if (result === null || pos !== tokens.length || !Number.isFinite(result)) return null;
  return Number(result.toFixed(9));
}
